import React, { useCallback, useEffect, useRef } from 'react'
import type { PlayerEventBase, TimeChangedEvent, UserInteractionEvent } from 'bitmovin-player'
import classNames from 'classnames'
import { useHlsInstance } from '@/hooks/useHlsInstance'
import { useAngelPlayerAPI } from '@/organisms/AngelPlayer/AngelPlayerContext'
import type { AngelPlayerEvent } from '@/organisms/AngelPlayer/AngelPlayerContext/AngelPlayerContext'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { PlayerEvent } from '../BitmovinPlayer/types'
import type { Asset, PlayerOverlay } from '../BitmovinPlayer/types'
import { HlsPlayerController } from './HlsPlayerController'

interface HlsPlayerProps {
  asset: Asset
  autoplay?: boolean
  aspectClassName?: string
  overlays?: PlayerOverlay[]
  isMuted?: boolean
}

/**
 * HLS.js-based video player component.
 *
 * Phase 1: Basic HLS playback with native <video> element.
 * No custom UI — uses browser-native controls as a placeholder.
 * Custom UI will be built in Phases 2-5.
 */
export const HlsPlayer: React.FC<HlsPlayerProps> = ({
  asset,
  autoplay,
  aspectClassName = 'w-full h-full',
  overlays,
  isMuted,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const controllerRef = useRef<HlsPlayerController | null>(null)
  const mountedRef = useRef(false)
  const hasPlayedRef = useRef(false)
  const timeUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { hls, isReady } = useHlsInstance({
    videoRef,
    url: asset.sourceConfig.hls,
    muted: isMuted,
    // Phase 6: DRM config will be passed here via hlsConfig
  })

  const {
    setController,
    setIsSourceLoaded,
    onPlaying,
    onPaused,
    onSeek,
    onSeeked,
    onTimeChanged,
    onSourceLoaded,
    onPlaybackFinished,
    onReady,
    saveWatchPosition,
  } = useAngelPlayerAPI()

  // Initialize controller and wire events when HLS.js is ready
  const setupController = useCallback(() => {
    const video = videoRef.current
    if (!video || !hls) return

    const controller = new HlsPlayerController(video, hls, asset, autoplay)
    controllerRef.current = controller
    setController?.(controller)

    // Wire HTML5 video events to AngelPlayerContext callbacks.
    video.addEventListener('playing', () => {
      hasPlayedRef.current = true
      onPlaying()
    })

    video.addEventListener('pause', () => {
      onPaused(
        createUserInteractionEvent(PlayerEvent.Paused, 'ui'),
        createAngelPlayerEvent(video, hasPlayedRef.current),
      )
    })

    video.addEventListener('seeking', () => {
      onSeek()
    })

    video.addEventListener('seeked', () => {
      onSeeked(
        createUserInteractionEvent(PlayerEvent.Seeked, 'ui'),
        createAngelPlayerEvent(video, hasPlayedRef.current),
      )
    })

    video.addEventListener('ended', () => {
      onPlaybackFinished()
    })

    // Emit timeChanged at a regular interval (matching Bitmovin's ~250ms updates)
    timeUpdateIntervalRef.current = setInterval(() => {
      if (!video.paused && !video.ended) {
        onTimeChanged?.(createTimeChangedEvent(video.currentTime))
      }
    }, 250)
  }, [hls, asset, autoplay, onPlaying, onPaused, onSeek, onSeeked, onTimeChanged, onPlaybackFinished, setController])

  // Notify context when source is ready
  useEffect(() => {
    if (!isReady) return
    onSourceLoaded(createPlayerEvent(PlayerEvent.SourceLoaded))
    setIsSourceLoaded?.(true)
    onReady()
  }, [isReady, onSourceLoaded, setIsSourceLoaded, onReady])

  useEffect(() => {
    // React strict mode in development mounts components twice.
    // Skip the first mount to avoid double initialization.
    if (isDevelopmentEnvironment() && !mountedRef.current) {
      mountedRef.current = true
      return
    }

    setupController()

    const handleSaveWatchPosition = () => saveWatchPosition()
    window.addEventListener('beforeunload', handleSaveWatchPosition)

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current)
        timeUpdateIntervalRef.current = null
      }
      controllerRef.current?.destroy()
      controllerRef.current = null
      hasPlayedRef.current = false
      setIsSourceLoaded?.(false)
      window.removeEventListener('beforeunload', handleSaveWatchPosition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only run on mount/unmount
  }, [])

  // Handle asset changes (e.g., switching episodes)
  useEffect(() => {
    if (!controllerRef.current) return
    // The controller was already initialized with the initial asset in the mount effect.
    // This effect handles subsequent asset changes only.
    const controller = controllerRef.current
    if (controller.getCurrentTime() !== undefined) {
      controller.load({
        sourceConfig: asset.sourceConfig,
        name: asset.name,
        framesPerSecond: asset.frameRate,
        adjustmentFactor: asset.adjustmentFactor,
        framesDroppedAtFullMinute: asset.framesDroppedEachMinute,
        frameDuration: 1 / asset.frameRate,
        offsetToMidFrame: 0,
        position: asset.position,
      })
    }
  }, [asset])

  return (
    <div id="hlsjs-player" className={classNames([aspectClassName, 'relative'])}>
      {/* Phase 1: Native browser controls. Custom UI replaces this in Phases 2-5. */}
      <video ref={videoRef} className="h-full w-full" controls playsInline />
      {overlays?.map(({ key, Component }) => (
        <Component key={key} />
      ))}
    </div>
  )
}

/**
 * Creates a minimal PlayerEventBase-compatible object for HLS.js events.
 * The AngelPlayerContext only reads `timestamp` and `type` from base events.
 */
function createPlayerEvent(type: PlayerEvent): PlayerEventBase {
  return { timestamp: Date.now(), type }
}

/**
 * Creates a UserInteractionEvent-compatible object for HLS.js events.
 * The AngelPlayerContext reads `issuer` to distinguish event sources:
 * - `'ui'` — user-initiated (e.g., clicking pause)
 * - `'ui-seek'` — user-initiated seek (checked by context to set seeking state)
 * - `'api'` — programmatic (Bitmovin's default when no issuer is specified)
 */
function createUserInteractionEvent(type: PlayerEvent, issuer: string): UserInteractionEvent {
  return { timestamp: Date.now(), type, issuer }
}

/**
 * Creates a TimeChangedEvent-compatible object from the current video time.
 */
function createTimeChangedEvent(time: number): TimeChangedEvent {
  return { timestamp: Date.now(), type: PlayerEvent.TimeChanged, time, issuer: 'api' }
}

/**
 * Creates an AngelPlayerEvent from the current video state.
 */
function createAngelPlayerEvent(video: HTMLVideoElement, hasPlayed: boolean): AngelPlayerEvent {
  return { hasPlayed, time: video.currentTime }
}
